import * as React from 'react';

import { controlBase, controlStates } from '../../lib/control';
import { isMultiChoiceType, type AnswerDraft } from '../../lib/form-answers';
import { cn } from '../../lib/utils';
import { FORM_RATING_MAX, type FormQuestion } from '../../types/form-question';
import Select from '../select';
import { Input } from '../shadcn/input';
import { Textarea } from '../shadcn/textarea';

import { ChoiceList } from './choice-list';
import { DatePicker } from './date-picker';
import { MultiSelect } from './multi-select';
import { RatingScale } from './rating-scale';

const RATINGS = Array.from({ length: FORM_RATING_MAX }, (_, i) => String(i + 1));

/** What a form field hands its control. Anything that takes these can sit inside one. */
type FieldProps = {
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onBlur?: () => void;
};

/**
 * The control for one question of an event form — the registration form or the feedback form —
 * chosen by the question's type.
 *
 * Bare, like an Input: the label, hint and error belong to whatever wraps it. One-box questions
 * draw in the shared control box — Input, Textarea, and a native select cut from the same
 * `lib/control` — unless `boxClassName` hands in the app's own box, in which case a dropdown is
 * the shared Select, whose field skin is that box. Radios, checkboxes and the 1–5 rating draw as
 * a row of options, which a legend labels rather than a label — `isGroupType` says which.
 *
 * It takes what a Field passes down, so it can sit inside either app's Field. In a row of options
 * the description and invalid state go on every option, since each is focused on its own.
 *
 * `rich` swaps the browser's own controls for the site's: the shared Select, MultiSelect,
 * ChoiceList, DatePicker and RatingScale. The client's guest forms set it; the admin's do not.
 */
export function QuestionInput({
  question,
  value = '',
  onChange = () => {},
  disabled,
  boxClassName,
  rich = false,
  onBlur,
  ...field
}: FieldProps & {
  question: FormQuestion;
  /** Optional only so a `<Field name>` can supply it; set it yourself outside one. */
  value?: AnswerDraft;
  /** Optional only so a `<Field name>` can supply it; set it yourself outside one. */
  onChange?: (next: AnswerDraft) => void;
  disabled?: boolean;
  /** Replaces the shared control box with the app's own: the admin's `inputCls`. */
  boxClassName?: string;
  /** Draw choices, dates and ratings with the site's own controls rather than native ones. */
  rich?: boolean;
}) {
  const name = React.useId();
  const text = Array.isArray(value) ? '' : value;
  const picks = Array.isArray(value) ? value : [];
  const blur = onBlur ? () => onBlur() : undefined;

  if (rich) {
    const options = question.options.map((option) => ({ value: option, label: option }));
    // A group has no one element a label can name, so it carries the question itself.
    const shared = { ...field, onBlur, disabled, 'aria-label': question.label };
    switch (question.type) {
      case 'select':
        return (
          <Select
            variant="field"
            value={text}
            options={options}
            onChange={onChange}
            placeholder="Choose one…"
            {...shared}
          />
        );
      case 'multiselect':
        return <MultiSelect value={picks} options={options} onChange={onChange} {...shared} />;
      case 'radio':
        return <ChoiceList value={text} options={options} onChange={onChange} {...shared} />;
      case 'checkbox':
        return (
          <ChoiceList multiple value={picks} options={options} onChange={onChange} {...shared} />
        );
      case 'rating':
        return <RatingScale value={text} onChange={onChange} {...shared} />;
      case 'date':
        return <DatePicker value={text} onChange={onChange} {...field} onBlur={onBlur} disabled={disabled} />;
      // Text, long text and number are already the shared boxes, drawn below.
    }
  }

  if (question.type === 'select' && !boxClassName) {
    return (
      <select
        className={cn(controlBase, controlStates, 'h-9')}
        value={text}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={blur}
        {...field}
      >
        <option value="">Choose one…</option>
        {question.options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (question.type === 'select') {
    return (
      <Select
        variant="field"
        value={text}
        options={question.options.map((option) => ({ value: option, label: option }))}
        onChange={onChange}
        placeholder="Choose one…"
        disabled={disabled}
        aria-label={question.label}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        onBlur={onBlur}
      />
    );
  }

  if (question.type === 'rating') {
    return (
      <div className="flex flex-wrap gap-1.5">
        {RATINGS.map((rating, index) => (
          <label
            key={rating}
            className={cn(
              'flex size-10 cursor-pointer items-center justify-center rounded-input border border-input bg-raised text-base tabular-nums transition-colors',
              'has-checked:border-brand-edge has-checked:bg-brand-wash has-checked:font-semibold has-checked:text-brand-strong',
              'has-focus-visible:ring-3 has-focus-visible:ring-brand/50',
              'has-disabled:cursor-not-allowed has-disabled:text-ink-disabled',
            )}
          >
            <input
              type="radio"
              className="sr-only"
              name={name}
              value={rating}
              checked={text === rating}
              disabled={disabled}
              onChange={() => onChange(rating)}
              onBlur={blur}
              {...field}
              id={index === 0 ? field.id : undefined}
            />
            {rating}
          </label>
        ))}
      </div>
    );
  }

  if (question.type === 'radio' || isMultiChoiceType(question.type)) {
    const multi = isMultiChoiceType(question.type);
    return (
      <div className="flex flex-col gap-2.5">
        {question.options.map((option, index) => (
          <label
            key={option}
            className="flex cursor-pointer items-center gap-2.5 text-sm md:text-base has-disabled:cursor-not-allowed has-disabled:text-ink-disabled"
          >
            <input
              type={multi ? 'checkbox' : 'radio'}
              className="size-5 shrink-0 accent-brand-edge"
              name={name}
              value={option}
              checked={multi ? picks.includes(option) : text === option}
              disabled={disabled}
              onChange={(e) =>
                onChange(
                  !multi
                    ? option
                    : e.target.checked
                      ? // Kept in the order the form lists them, whatever order they were ticked.
                        question.options.filter((o) => o === option || picks.includes(o))
                      : picks.filter((pick) => pick !== option),
                )
              }
              onBlur={blur}
              {...field}
              id={index === 0 ? field.id : undefined}
            />
            {option}
          </label>
        ))}
      </div>
    );
  }

  if (question.type === 'textarea') {
    const props = {
      rows: 3,
      maxLength: 5_000,
      value: text,
      disabled,
      onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value),
      onBlur: blur,
      ...field,
    };
    return boxClassName ? (
      <textarea className={boxClassName} {...props} />
    ) : (
      <Textarea {...props} />
    );
  }

  const props = {
    type: question.type === 'number' ? 'number' : question.type === 'date' ? 'date' : 'text',
    inputMode: question.type === 'number' ? ('decimal' as const) : undefined,
    maxLength: question.type === 'text' ? 500 : undefined,
    value: text,
    disabled,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
    onBlur: blur,
    ...field,
  };
  return boxClassName ? <input className={boxClassName} {...props} /> : <Input {...props} />;
}
